import React, { useEffect, useMemo, useRef, useState } from 'react';
import { parseTriplet } from '../../../theme/contrast';
import { SOFT_SWATCHES, SWATCH_COLUMNS, WARM_SWATCHES } from './palette';

/**
 * Editing controls for the Customize page.
 *
 * The app has no hex input anywhere - the two existing pickers are HSL sliders
 * that cannot express a brand color exactly - so this adds one, alongside a
 * native swatch. Values are stored as `R G B` triplets, which is what the token
 * contract uses so alpha modifiers keep working.
 *
 * These controls are deliberately sized in fixed px. A control that resizes
 * while you drag the thing it controls is unusable, and this page can change the
 * root font size and the spacing unit.
 */

export const CHROME_FONT_SIZE = '13px';

export function tripletToHex(triplet: string): string {
  const rgb = parseTriplet(triplet);
  if (!rgb) return '#000000';
  return `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

export function hexToTriplet(hex: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const full = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

interface RowProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
  /** Token name, so the preview inspector can scroll straight to this row. */
  anchor?: string;
  /** Marked on arrival, because reaching the right control you cannot pick out
      of forty identical rows is the same as not reaching it. */
  flash?: boolean;
}

/** The label/description/control row used across the app's settings surfaces. */
export const Row: React.FC<RowProps> = ({ label, hint, children, anchor, flash }) => (
  <div
    id={anchor ? `control-${anchor}` : undefined}
    className={`flex items-center justify-between gap-4 py-2 ${flash
      ? '-mx-2 rounded bg-amber-100 px-2 ring-1 ring-amber-400 dark:bg-amber-900/40 dark:ring-amber-600'
      : ''}`}
  >
    <div className="min-w-0">
      <div className="text-sm font-medium text-heading">{label}</div>
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

interface ColorFieldProps {
  label: string;
  hint?: string;
  /** Token name, so the preview inspector can scroll straight to this control. */
  anchor?: string;
  /** Briefly marked, because arriving at the right control you cannot find is
      the same as not arriving. */
  flash?: boolean;
  /** Current effective value as an `R G B` triplet. */
  value: string;
  /** True when the theme sets this explicitly (as opposed to inheriting). */
  overridden: boolean;
  onChange: (triplet: string) => void;
  onReset: () => void;
}

export const ColorField: React.FC<ColorFieldProps> = ({
  label, hint, value, overridden, onChange, onReset, anchor, flash,
}) => {
  const hex = useMemo(() => tripletToHex(value), [value]);
  const valid = parseTriplet(value) !== null;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape. Without this the panel is a trap on a
  // page that already has a lot of small controls competing for the same space.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = (nextHex: string) => {
    const triplet = hexToTriplet(nextHex);
    if (triplet) onChange(triplet);
  };

  return (
    <Row label={label} hint={hint} anchor={anchor} flash={flash}>
      <div className="relative flex items-center gap-2" ref={wrapRef}>
        {overridden && (
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-blue-600 hover:underline dark:text-blue-400"
            title="Reset to the inherited value"
          >
            reset
          </button>
        )}
        <input
          type="text"
          aria-label={`${label} hex value`}
          value={valid ? hex : ''}
          placeholder="#rrggbb"
          onChange={(e) => apply(e.target.value)}
          spellCheck={false}
          className="w-24 rounded border border-gray-300 bg-white px-2 py-1 tabular-nums
                     text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          style={{ fontSize: CHROME_FONT_SIZE }}
        />
        <input
          type="color"
          aria-label={`${label} swatch`}
          value={hex}
          onChange={(e) => apply(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-600"
          style={{ width: '32px', height: '28px', padding: '2px' }}
        />
        <button
          type="button"
          aria-label={`${label} preset colors`}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="rounded border border-gray-300 px-1 text-gray-600 hover:bg-gray-100
                     dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          style={{ height: '28px', fontSize: '10px', lineHeight: '28px' }}
          title="Preset colors"
        >
          ▾
        </button>

        {open && (
          <div
            className="absolute right-0 top-full z-20 mt-1 rounded border border-gray-300
                       bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
            style={{ width: '212px' }}
          >
            {([['Warm', WARM_SWATCHES], ['Soft', SOFT_SWATCHES]] as const).map(([title, list]) => (
              <div key={title} className="mb-1 last:mb-0">
                <div
                  className="mb-1 font-semibold uppercase tracking-wide text-muted"
                  style={{ fontSize: '10px' }}
                >
                  {title}
                </div>
                <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${SWATCH_COLUMNS}, 1fr)` }}>
                  {list.map((swatch) => {
                    const selected = valid && hex.toLowerCase() === swatch.hex.toLowerCase();
                    return (
                      <button
                        key={swatch.hex}
                        type="button"
                        title={`${swatch.name} ${swatch.hex}`}
                        aria-label={swatch.name}
                        onClick={() => { apply(swatch.hex); setOpen(false); }}
                        className={`rounded border ${selected
                          ? 'border-blue-500 ring-2 ring-blue-500'
                          : 'border-gray-300 dark:border-gray-600'}`}
                        style={{ width: '26px', height: '26px', background: swatch.hex }}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            <p
              className="mt-1 text-muted"
              style={{ fontSize: '10px' }}
            >
              Rows are tint, tone and shade. Warm and soft match row for row, so a
              theme can be shifted between them.
            </p>
          </div>
        )}
      </div>
    </Row>
  );
};

interface SliderFieldProps {
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  overridden: boolean;
  onChange: (value: number) => void;
  onReset: () => void;
}

export const SliderField: React.FC<SliderFieldProps> = ({
  label, hint, value, min, max, step = 1, suffix = '', overridden, onChange, onReset,
}) => (
  <Row label={label} hint={hint}>
    <div className="flex items-center gap-2">
      {overridden && (
        <button
          type="button"
          onClick={onReset}
          className="text-xs text-blue-600 hover:underline dark:text-blue-400"
        >
          reset
        </button>
      )}
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: '140px' }}
      />
      <span
        className="tabular-nums text-gray-600 dark:text-gray-300"
        style={{ fontSize: CHROME_FONT_SIZE, width: '56px', display: 'inline-block' }}
      >
        {value}
        {suffix}
      </span>
    </div>
  </Row>
);

export const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title, children,
}) => (
  <section className="border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700">
    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">
      {title}
    </h3>
    {children}
  </section>
);
